/// <reference types="vite/client" />

/**
 * @fileoverview App Sidebar Component
 *
 * Provides an overlay-only sidebar for mobile-first user applications.
 * Always uses overlay mode, never permanently shown.
 *
 * @module layout/app/AppSidebar
 */

import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Badge } from '@xanectech/ui/components';
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarHeader,
  useSidebar,
} from '@xanectech/ui/components';
import { AppSidebarProps, MenuItem } from './app-config';
import { useEncryptionStatus } from '@xanectech/auth';
import { createLogger } from '@xanectech/lib';
import { log } from 'node:console';

const logger = createLogger('AppSidebar');

// Re-export MenuItem for backwards compatibility
export type { MenuItem };

/**
 * AppSidebar Component
 *
 * Mobile-first sidebar with:
 * - Always overlay mode (never permanent)
 * - Backdrop/overlay when open
 * - Auto-close on navigation
 * - Slide in from left
 * - Same behavior on mobile and desktop
 *
 * @component
 */
export function AppSidebar({
  logoSrc = (import.meta.env.VITE_WEBLOGO as string | undefined) ||
    '/xanectech.png',
  logoAlt = 'Xanec Technologies Logo',
  headerTitle,
  headerSubtitle,
  groupLabel,
  menuItems,
  products,
}: AppSidebarProps) {
  logger.debug('Rendering AppSidebar with props:', {
    logoSrc,
    logoAlt,
    headerTitle,
    headerSubtitle,
    groupLabel,
    menuItems,
    products,
  });

  const { setOpenMobile } = useSidebar();
  const location = useLocation();
  const navigate = useNavigate();
  const { isUnlocked } = useEncryptionStatus();

  const currentPath = location.pathname;
  const isActive = (path: string) => {
    // Exact match for the path
    if (currentPath === path) return true;

    // For root paths like /admin, /company, /app, /help - only match exactly
    if (
      path === '/admin' ||
      path === '/company' ||
      path === '/app' ||
      path === '/help'
    ) {
      return false;
    }

    // For non-root paths, allow matching child routes
    return currentPath.startsWith(path + '/') || currentPath === path;
  };

  const handleNavigate = (path: string) => {
    void navigate(path);
    // Auto-close sidebar after navigation (mobile pattern)
    setOpenMobile(false);
  };

  // Filter menu items based on lock state
  const visibleMenuItems = menuItems.filter((item) => {
    if (!isUnlocked && item.shownWhenLocked !== true) {
      logger.debug(`Hiding menu item "${item.title}" because it is locked.`);
      return false;
    } else if (products && item.requiredProducts) {
      // If we have product info, check if item requires products and if user has access
      return item.requiredProducts.some((product) =>
        products.includes(product),
      );
    }
    return true;
  });

  // Filter menu items based on product access
  // If item has requiredProducts, check if user has access to any of them
  const hasProductAccess = (requiredProducts: string[] | undefined) => {
    if (!requiredProducts || requiredProducts.length === 0) return true;
    if (!products) return false; // If we don't have product info, hide items that require products
    return requiredProducts.some((product) => products.includes(product));
  };

  return (
    <Sidebar variant='sidebar' collapsible='offcanvas' className='border-r'>
      <SidebarHeader className='border-b px-3 h-14 justify-center'>
        <div className='flex items-center space-x-3'>
          <img
            src={logoSrc}
            alt={logoAlt}
            className='h-10 w-10 sm:h-12 sm:w-12 object-contain flex-shrink-0 cursor-pointer'
            onClick={() => handleNavigate('/')}
          />
          <div>
            <h2 className='text-lg font-semibold'>{headerTitle}</h2>
            {/* <p className='text-xs text-muted-foreground'>{headerSubtitle}</p> */}
          </div>
        </div>
      </SidebarHeader>

      <SidebarContent className='px-2 py-4'>
        <SidebarGroup>
          <SidebarGroupLabel>{groupLabel}</SidebarGroupLabel>

          <SidebarGroupContent>
            <SidebarMenu className='space-y-1'>
              {visibleMenuItems.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton
                    asChild
                    isActive={isActive(item.url)}
                    className={`rounded-lg transition-all duration-200 ${
                      isActive(item.url)
                        ? 'bg-primary text-primary-foreground hover:bg-primary/90 shadow-sm'
                        : 'hover:bg-accent hover:text-primary'
                    }`}
                  >
                    <button
                      onClick={() => handleNavigate(item.url)}
                      className='flex items-center w-full text-left px-3 py-2'
                    >
                      <item.icon className='h-4 w-4' />
                      <span className='ml-3'>{item.title}</span>
                      {item.badge && item.badge > 0 && (
                        <Badge
                          variant='destructive'
                          className='ml-auto h-5 min-w-5 flex items-center justify-center px-1 text-xs'
                        >
                          {item.badge > 99 ? '99+' : item.badge}
                        </Badge>
                      )}
                    </button>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
    </Sidebar>
  );
}
